import { FC } from 'react'

import { FilePond, registerPlugin } from 'react-filepond'
import FilePondLocalePtBR from 'filepond/locale/pt-br.js';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import { FilePondFile, ProcessServerConfigFunction } from 'filepond';
import { MetadadosArquivo } from './model/MetadadosArquivo';
import { EnderecoArquivo } from './model/EnderecoArquivo';
import { FastField, FieldAttributes, FieldProps } from 'formik';
import { FuncoesUploadArquivo } from './InterfacesUpload';

import styled from 'styled-components';
import 'filepond/dist/filepond.min.css'


registerPlugin(FilePondPluginFileValidateSize);

interface UploadFieldProps extends FieldAttributes<any> {
  gerarEnderecoEnvio: (metadados: MetadadosArquivo) => Promise<EnderecoArquivo>;
}

export const UploadField: FC<UploadFieldProps> = ({gerarEnderecoEnvio, ...props}) => {
  return <FastField {...props}>
    {({field, form}: FieldProps) => {
      const funcoes: FuncoesUploadArquivo = {
        gerarEnderecoEnvio: gerarEnderecoEnvio,
        // A responsabilidade de confirmação é do processo de envio
        // do formulário pelo Formik
        consolidarArquivo: async (id) => {}
      }

      const onUpdateFiles = (files: FilePondFile[]) => {
        // Causar verificação da mensagem somente quando salvar
        form.setFieldValue(field.name, files, false);
      }

      return <Upload {...props} onUpdateFiles={onUpdateFiles} funcoes={funcoes} />
    }}
  </FastField>
}

export interface UploadProps {
  funcoes: FuncoesUploadArquivo;
  onUpdateFiles: (files: FilePondFile[]) => void;
}

export const Upload: FC<UploadProps> = ({funcoes, onUpdateFiles}) => {
  const server = {
    process: gerarCustomProcess(funcoes)
  }

  return (
    <Browse as={FilePond} {...FilePondLocalePtBR}
      labelIdle={`<span class="filepond--label-action">Clique para adicionar arquivos</span> ou arraste e solte-os aqui`}
      server={server}
      onupdatefiles={onUpdateFiles}
      allowMultiple={true}
      maxParallelUploads={2}

      // FIXME: Permitir exclusão de arquivo não confirmado
      //        pelo usuário que envio
      allowRevert={false}
      name="files"
      credits={false}

      // Filesize plugin
      maxFileSize="1MB"
    />
  )
}

const Browse = styled.div`
/* Raiz corresponde a .filepond--root */
margin: 1em 0 0 0;

.filepond--panel-root {
  background-color: ${({theme}) => theme.background.nivel_0};
  border: 1px solid ${({theme}) => theme.background.borda};
}

.filepond--drop-label,
.filepond--drop-label.filepond--drop-label label {
  cursor: pointer;
}

.filepond--drop-label.filepond--drop-label label {
  font-family: var(--font-text);
  font-size: .9em;
}
.filepond--drop-label.filepond--drop-label label,
.filepond--file {
  font-family: var(--font-text);
}

[data-filepond-item-state*='processing'] .filepond--item-panel {
    background-color: var(--color-gray-300);
}

[data-filepond-item-state*='processing-complete'] .filepond--item-panel {
    background-color: #42526E;
}

[data-filepond-item-state*='error'] .filepond--item-panel
[data-filepond-item-state*='error'] .filepond--item-panel,
[data-filepond-item-state*='invalid'] .filepond--item-panel {
    background-color: var(--color-red-300);
}

.filepond--file-status .filepond--file-status-sub,
.filepond--file-info .filepond--file-info-sub {
  opacity: 0.8;
}
`;


function gerarCustomProcess(funcoes: FuncoesUploadArquivo): ProcessServerConfigFunction {
  const customProcess: ProcessServerConfigFunction = async function(fieldName, file, metadata, load, error, progress, abort) {
    const formData = new FormData();
    formData.append(fieldName, file, file.name);
  
    const metadados: MetadadosArquivo = {
      nome: file.name,
      tamanho: file.size,
      mimeType: file.type
    }
  
    let enderecoArquivo: EnderecoArquivo = {idArquivo: '', url: ''};
    try {
      enderecoArquivo = await funcoes.gerarEnderecoEnvio(metadados);
    } catch (e) {
      error('Não foi iniciar o envio do anexo, tente novamente mais tarde');
      return;
    }

    const request = new XMLHttpRequest();
    request.open('PUT', enderecoArquivo.url);
    // request.setRequestHeader('Access-Control-Allow-Origin', '*');
    
    request.upload.onprogress = (e) => progress(e.lengthComputable, e.loaded, e.total);
 
    request.onload = async function() {
      if (request.status >= 200 && request.status < 300) {
        await funcoes.consolidarArquivo(enderecoArquivo.idArquivo);
        // Preencher idServer com o id do arquivo
        load(enderecoArquivo.idArquivo);
      } else {
        // FIXME - Descobrir o que fazer nesta situação em particular
        error('Não foi possível completar o envio do anexo');
      }
    };
    
    request.send(file);

    return {
      abort: () => {
        request.abort();
        abort();
      }
    }
  }

  return customProcess;
}
